import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { BuildingActions, AuthActions } from '../../store';
import { HELP_CATEGORY, ACCOUNT_CATEGORY, HELP_SUBCATEGORY, SETTINGS_CATEGORY } from '../NavBar/navConstants';
import { ConnectedEnergyUsageWithRouter } from '../EnergyUsage';
import { ConnectedDiagnosticsWithRouter } from '../Diagnostics';
import { ConnectedEventLogWithRouter } from '../EventLog';
import { ConnectedAlertConfigurationWithRouter } from '../AlertConfiguration';
import { ConnectedThermostatSetup } from '../ThermostatSetup';
import { ConnectedThermostatStatus } from '../ThermostatStatus';
import { ConnectedSuiteGraphPageWithRouter } from '../SuiteGraphPage';
import { ConnectedAlertListWithRouter } from '../AlertList/AlertList';
import { getDataPointsFromUrlParams } from '../../utils/urlParams';
import { config } from '../../config';

const { REACT_APP_ID } = config;

export class Page extends Component {
  async componentDidMount() {
    const {
      building,
      category,
      subcategory = '',
      selectedBuilding,
      getBuildingList,
      setSelectedBuilding,
      setNavigationParams,
      setInitialConditions,
    } = this.props;

    this.validateVersion();

    if (building && building !== selectedBuilding.identifier) {
      await getBuildingList();
      await setSelectedBuilding(building);

      return setInitialConditions();
    }

    if (selectedBuilding.navigation.length !== 0) {
      if (category === 'help') {
        const updatedCategory = HELP_CATEGORY;
        const updatedSubCategory = HELP_SUBCATEGORY;
        return setNavigationParams(updatedCategory, updatedSubCategory);
      } else if (category === 'account') {
        const updatedCategory = ACCOUNT_CATEGORY;
        const filteredSubCategory = updatedCategory.sub_categories.filter((navSubCat) => navSubCat.id === subcategory);
        const updatedSubCategory = filteredSubCategory ? filteredSubCategory[0] : updatedCategory.sub_categories[0];
        return setNavigationParams(updatedCategory, updatedSubCategory);
      } else if (category === 'settings') {
        const updatedCategory = SETTINGS_CATEGORY;
        const filteredSubCategory = updatedCategory.sub_categories.filter((navSubCat) => navSubCat.id === subcategory);
        const updatedSubCategory = filteredSubCategory ? filteredSubCategory[0] : updatedCategory.sub_categories[0];

        return setNavigationParams(updatedCategory, updatedSubCategory);
      } else {
        const updatedCategory = selectedBuilding.navigation.filter((navCat) => navCat.id === category)[0];
        const filteredSubCategory = updatedCategory.sub_categories.find((navSubCat) => navSubCat.id === subcategory);
        const updatedSubCategory = filteredSubCategory ? filteredSubCategory : updatedCategory.sub_categories[0];

        return setNavigationParams(updatedCategory, updatedSubCategory);
      }
    }

    const updatedCategory = selectedBuilding.navigation.filter((navCat) => navCat.id === category)[0];
    const updatedSubCategory = updatedCategory.sub_categories.filter((navSubCat) => navSubCat.id === subcategory)[0];
    return setNavigationParams(updatedCategory, updatedSubCategory);
  }

  validateVersion = async () => {
    const { validateVersion, createVersion } = this.props;
    const versionNumber = process.env.REACT_APP_VERSION;
    const appId = REACT_APP_ID;

    // Validate the version first to see if it's the latest version
    // If it's not, then we see if current version is higher than latest version
    // If it is higher, we update the latest version using auth/api/versions and POST
    // If it is lower, we invalidate cache and refresh

    const versionValidateObj = await validateVersion(versionNumber, appId);

    if (typeof versionValidateObj === 'string') {
      // Refresh
      if (window.confirm(`There was an error checking the version: ${versionValidateObj}`)) {
        window.location.reload();
      }
    } else if (typeof versionValidateObj === 'object') {
      const { is_latest: isLatest, should_create: shouldCreate } = versionValidateObj;

      if (!isLatest) {
        if (shouldCreate) {
          createVersion(true, appId, versionNumber);
        } else {
          // Refresh
          if (window.confirm('There is a new app available. Refresh?')) {
            window.location.reload();
          }
        }
      }
    }
  };

  renderSwitch(category, subcategory) {
    switch (category) {
      case 'electrical-usage':
      case 'steam-usage':
      case 'gas-usage':
        return <ConnectedEnergyUsageWithRouter />;
      case 'diagnostics':
        return <ConnectedDiagnosticsWithRouter />;
      case 'thermostats':
        if (subcategory === 'setup') {
          return <ConnectedThermostatSetup />;
        }
        if (subcategory === 'status') {
          return <ConnectedThermostatStatus />;
        }
        if (subcategory === 'suite-graphs') {
          return <ConnectedSuiteGraphPageWithRouter />;
        }
        break;
      case 'alerts':
        if (subcategory === 'alert-incidents') {
          return <ConnectedEventLogWithRouter />;
        }
        if (subcategory === 'alert-setup') {
          return <ConnectedAlertConfigurationWithRouter />;
        }
        if (subcategory === 'alert-list') {
          return <ConnectedAlertListWithRouter />;
        }
        break;
      default:
        return null;
    }
  }

  render() {
    const {
      building,
      category,
      subcategory,
      selectedBuilding: { name: selectedBuildingName, identifier: selectedBuildingIdentifier },
      navigationParams: {
        selectedCategory: { name: selectedCategoryName, id: selectedCategoryId },
        selectedSubCategory,
      } = {
        selectedCategory: { name: '', id: '' },
        selectedSubCategory,
      },
      children,
    } = this.props;

    const pageDataPoints = getDataPointsFromUrlParams(window.location.search);

    if (selectedCategoryId === 'diagnostics' && pageDataPoints.length > 0) {
      // Set the customDataPoints as well as the lineColor variables to the selected ones
      let sessionCustomDataPoints = JSON.parse(window.sessionStorage.getItem('customDataPoints')) || {};

      sessionCustomDataPoints = {
        ...sessionCustomDataPoints,
        [selectedSubCategory.id]: pageDataPoints,
      };

      window.sessionStorage.setItem('customDataPoints', JSON.stringify(sessionCustomDataPoints));
    }

    if (!selectedCategoryId || !selectedSubCategory) {
      return null;
    }

    if (['help', 'account', 'settings'].indexOf(category) !== -1) {
      document.title = `${category}`;

      return children;
    }

    if (
      building === selectedBuildingIdentifier &&
      category === selectedCategoryId &&
      subcategory === selectedSubCategory.id
    ) {
      document.title = `${selectedBuildingName} ${selectedCategoryName} ${selectedSubCategory.id}`;

      return this.renderSwitch(category, subcategory);
    }

    return null;
  }
}

Page.propTypes = {
  key: PropTypes.string,
  building: PropTypes.string,
  category: PropTypes.string.isRequired,
  subcategory: PropTypes.string,
  setInitialConditions: PropTypes.func,
};

Page.defaultProps = {
  subcategory: '',
};

export function mapStateToProps(state) {
  return {
    selectedBuilding: state.buildingReducer.selectedBuilding,
    navigationParams: state.buildingReducer.navigationParams,
  };
}

const mapDispatchToProps = {
  getBuildingList: BuildingActions.getBuildingList,
  setSelectedBuilding: BuildingActions.setSelectedBuilding,
  setNavigationParams: BuildingActions.setNavigationParams,
  setInitialConditions: BuildingActions.setInitialConditions,
  validateVersion: AuthActions.validateVersion,
  createVersion: AuthActions.createVersion,
};

const ConnectedPage = connect(mapStateToProps, mapDispatchToProps)(Page);
export { ConnectedPage };
